import styled from 'styled-components';

import { categoryStatus, RollupAction } from 'components/inboundReferrals/referralProgress';
import { actionStatusDisplay, Pill, TONE_STYLES, workflowStatusDisplay } from 'components/inboundReferrals/StatusBadge';
import { AdminRunStep, AdminWorkflowRun } from 'models/AdminWorkflowRun';
import { ActionStatus, WorkflowStatus } from 'models/Workflow';
import { colors } from 'styles/theme/colors';

// Delegates rather than restating: a second copy of this map drifted once already. Step statuses
// are the same strings the referral page's ActionStatus uses, and its fallback handles anything new.
export function stepDisplay(status: string) {
  return actionStatusDisplay(status as ActionStatus);
}

// The referral page's own precedence, not a copy of it. Two copies is how this console and that
// page end up disagreeing about the same run, which is the one thing the ticket says must never
// happen — and it had already happened once with the labels.
export function rollup(steps: AdminRunStep[]): string {
  if (steps.length === 0) return 'pending';

  return categoryStatus(steps.map(asRollupAction));
}

// `stale` is the console's one addition and is deliberate: an out-of-date result is this page's
// whole subject and has no counterpart on the referral page. Folding it into effectiveStatus
// before the shared precedence runs keeps the precedence itself in one place.
function asRollupAction(step: AdminRunStep): RollupAction {
  return {
    status: step.status,
    effectiveStatus: step.stale ? 'flagged' : step.effectiveStatus,
    isCompleted: step.status === 'completed',
  };
}

// What the list's status column shows for a whole run. The run's own terminal status wins over
// the step rollup, the way referralProgress#overallListStatus short-circuits: a canceled run has
// a step set that rolls up to whatever it was doing when it stopped, and the list would disagree
// with the detail page about the same run.
export function runRollup(run: { status: string; steps: AdminRunStep[] }): string {
  if (run.status === 'canceled') return 'canceled';
  if (run.status === 'completed') return 'completed';

  return rollup(run.steps);
}

// One row per LANE, not one per attempt. A re-run chains `previous`, so a step that ran three
// times is three rows of the same name differing only by a muted "earlier attempt" — which reads
// as three separate steps. Chaining collapses each lineage to its latest instance and keeps the
// rest as its history.
//
// Grouped by the chain rather than by (type, lane): a fanned-out step has one lineage per
// facility or coverage, and a facet whose label is blank would otherwise merge two real lanes
// into one row.
export type AttemptChain<T extends { id: string; previousId: string | null }> = { latest: T; earlier: T[] };

export function attemptChains<T extends { id: string; previousId: string | null }>(steps: T[]): AttemptChain<T>[] {
  const byId = new Map(steps.map((step) => [step.id, step]));
  const superseded = new Set(steps.map((step) => step.previousId).filter(Boolean) as string[]);

  // Order is the API's, which is the contract's: filtering preserves it.
  return steps
    .filter((step) => !superseded.has(step.id))
    .map((latest) => {
      const earlier: T[] = [];
      // Guarded against a cycle: a corrupt chain should render short, not hang the page.
      for (let cur = byId.get(latest.previousId ?? ''); cur && !earlier.includes(cur); ) {
        earlier.push(cur);
        cur = byId.get(cur.previousId ?? '');
      }
      return { latest, earlier };
    });
}

// One segment per category, the shape the referral list already uses, rather than one per step:
// a real run carries up to 294 step rows and a bar with 294 segments says nothing.
export function categorySegments(steps: AdminRunStep[]) {
  const order: string[] = [];
  const byCategory = new Map<string, AdminRunStep[]>();
  steps.forEach((step) => {
    const key = step.categoryKey ?? 'ungrouped';
    if (!byCategory.has(key)) {
      byCategory.set(key, []);
      order.push(key);
    }
    byCategory.get(key)!.push(step);
  });

  return order.map((key) => ({ key, status: rollup(byCategory.get(key)!) }));
}

// Matches the list page's title (useAdminListPage's Title). A bare <h1> inherits the user-agent
// default of ~32px bold, which matches nothing else in admin.
export const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

export const Bar = styled.div`
  display: flex;
  gap: 2px;
`;

export const Segment = styled.div<{ $color: string }>`
  height: 9px;
  width: 27px;
  flex-shrink: 0;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

// The run id beside the name rather than beneath it. A support question arrives as a name, so the
// name is what gets scanned; the id is what gets pasted into a ticket afterwards. Both belong on
// the row, at different volumes.
export const RunId = styled.span`
  font-size: 12px;
  color: ${colors.black50};
  margin-left: 6px;
`;

export const Subtitle = styled.div`
  font-size: 12px;
  color: ${colors.black50};
`;

export const Warned = styled(Subtitle)`
  color: ${colors.orangeHover};
`;

export function StepStatus({ status }: { status: string }) {
  const display = stepDisplay(status);
  return <Pill $tone={display.tone}>{display.label}</Pill>;
}

// A concluded run is named by its OWN status, through the helper the detail header and the
// referral page both use. The rollup underneath describes STEPS, and steps have no archived
// concept to express — so a run a person archived read as "Canceled" here and "Archived" one
// click away, on the same run.
export function RunStatus({ run }: { run: AdminWorkflowRun }) {
  if (run.status === 'completed' || run.status === 'canceled') {
    const display = workflowStatusDisplay(run.status as WorkflowStatus, !!run.archivedAt);
    return <Pill $tone={display.tone}>{display.label}</Pill>;
  }

  return <StepStatus status={runRollup(run)} />;
}

export function ProgressBar({ steps }: { steps: AdminRunStep[] }) {
  const segments = categorySegments(steps);
  if (segments.length === 0) return <Subtitle>No steps</Subtitle>;

  return (
    <Bar>
      {segments.map((segment) => (
        <Segment key={segment.key} $color={TONE_STYLES[stepDisplay(segment.status).tone].accent} />
      ))}
    </Bar>
  );
}
